import styles from './trend-chart.module.scss';
import {
  formatTrendLabel,
  TREND_VIEWBOX,
  toTrendGeometry,
  toTrendScale,
  TrendPoint,
} from './trend-chart.utils';

interface TrendChartProps {
  points: TrendPoint[];
  /** Names the chart for assistive tech, since the SVG carries no axes. */
  label: string;
  className?: string;
}

/**
 * Inline SVG rather than recharts, as the mockup draws it. `preserveAspectRatio`
 * is left at its default: the mockup sets `none`, which stretches the stroke and
 * turns the dots into ellipses at any width other than the viewBox width.
 */
export default function TrendChart({ points, label, className }: TrendChartProps) {
  if (!points.length) return null;

  const geometry = toTrendGeometry(points);
  const { line, area, dots } = geometry;
  const { ticks, peak } = toTrendScale(points, geometry);

  return (
    <div className={className}>
      <svg
        aria-label={label}
        className={styles.chart}
        role="img"
        viewBox={`0 0 ${TREND_VIEWBOX.width} ${TREND_VIEWBOX.height}`}
      >
        {ticks.map((tick, index) => (
          <g key={tick.value}>
            <line
              className={styles.gridline}
              x1={0}
              x2={TREND_VIEWBOX.width}
              y1={tick.y}
              y2={tick.y}
            />
            {/* The top line's value is printed at the peak instead, which says which
                bucket reached it. With no peak to carry it, the line is labelled here
                rather than left nameless. */}
            {(index < ticks.length - 1 || !peak) && (
              <text className={styles.tick} x={0} y={tick.y - 4}>
                {tick.value}
              </text>
            )}
          </g>
        ))}
        <path className={styles.area} d={area} />
        <polyline className={styles.line} points={line} />
        {dots.map((dot) => (
          <circle className={styles.dot} cx={dot.x} cy={dot.y} key={dot.x} r={3.5} />
        ))}
        {peak && (
          <text className={styles.peak} x={peak.x} y={peak.y - 8}>
            {peak.value}
          </text>
        )}
      </svg>
      <div className={styles.labels}>
        <span>{formatTrendLabel(points[0].date)}</span>
        <span>{formatTrendLabel(points[points.length - 1].date)}</span>
      </div>
    </div>
  );
}
